import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Gift, Loader2, Minus, User } from 'lucide-react';
import { useAuth, type MeResponse } from '../lib/auth';
import { createCheckout, createPayPalCheckout, switchToYearly, getSiteConfig, resolveGiftRecipient, searchGiftRecipients, createGiftCheckout, createPayPalGiftCheckout, type GiftRecipient } from '../lib/api';
import { ApplePayMark, GooglePayMark, MastercardMark, PayPalMark, StripeMark, VisaMark } from '../components/BrandMarks';
import { cosmeticUrl } from '../lib/cosmeticUrl';
import TwitchSignInLabel from '../components/TwitchSignInLabel';

type Processor = 'card' | 'paypal';
type Plan = 'month' | 'year' | 'once';
type Mode = 'self' | 'gift';

const SUPPORTER_PRESETS = [1, 5, 10, 25, 50];

// What each way of paying unlocks. A one-time payment is the Supporter tier;
// monthly and yearly are the same Subscriber membership billed differently.
const PERKS: Array<{ label: string; once: boolean }> = [
  { label: 'Gold StreamNook badge', once: true },
  { label: 'Your 7TV paint as your profile background', once: true },
  { label: 'Animated Penrose badge', once: false },
  { label: 'Atmospheres for your profile and chat messages', once: false },
  { label: 'Monthly subscriber badges and tenure', once: false },
];

type Sub = NonNullable<MeResponse['subscription']>;

/** The member's live PAID plan, if any. A gift is not a plan they can change. */
function currentPlan(sub: Sub | null | undefined): { interval: 'month' | 'year'; processor: 'stripe' | 'paypal' } | null {
  if (!sub || (sub.status !== 'active' && sub.status !== 'past_due')) return null;
  if (sub.source !== 'stripe' && sub.source !== 'paypal') return null;
  return { interval: sub.billing_interval === 'year' ? 'year' : 'month', processor: sub.source };
}

function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function initialPlan(tier: string | null): Plan {
  if (tier === 'supporter') return 'once';
  return 'month';
}

export default function Support() {
  const { me, login, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>(params.get('tier') === 'gift' ? 'gift' : 'self');
  const [plan, setPlan] = useState<Plan>(() => initialPlan(params.get('tier')));
  const [supporterAmount, setSupporterAmount] = useState(5);
  const [supporterMin, setSupporterMin] = useState(1);
  const [monthlyPrice, setMonthlyPrice] = useState(5);
  // Null until /api/config names a yearly price, so a deployment without the
  // yearly plan never offers it.
  const [yearlyPrice, setYearlyPrice] = useState<number | null>(null);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [submitting, setSubmitting] = useState<Processor | 'switch' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSiteConfig()
      .then(cfg => {
        setSupporterMin(cfg.supporter_minimum_dollars);
        setSupporterAmount(prev => Math.max(prev, cfg.supporter_minimum_dollars));
        setMonthlyPrice(cfg.subscriber_minimum_dollars);
        setYearlyPrice(cfg.subscriber_yearly_dollars ?? null);
        setPaypalEnabled(cfg.paypal_enabled);
      })
      .catch(() => {});
  }, []);

  const sub = me?.subscription ?? null;
  const current = currentPlan(sub);
  const gifted = sub?.source === 'gift' && sub.status === 'active';
  const yearlySavings = yearlyPrice === null ? 0 : monthlyPrice * 12 - yearlyPrice;
  const freeMonths = Math.floor(yearlySavings / monthlyPrice);

  const pickPlan = (p: Plan) => {
    setPlan(p);
    setError(null);
  };

  const checkout = async (processor: Processor) => {
    if (!me) {
      login();
      return;
    }
    setError(null);
    setSubmitting(processor);
    try {
      const req = plan === 'once'
        ? { tier: 'supporter' as const, amount_dollars: supporterAmount }
        : { tier: 'subscriber' as const, amount_dollars: monthlyPrice, ...(plan === 'year' ? { interval: 'year' as const } : {}) };
      const { url } = processor === 'paypal' ? await createPayPalCheckout(req) : await createCheckout(req);
      window.location.assign(url);
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(code === 'already_subscribed' ? 'You already have an active membership.' : code);
      setSubmitting(null);
    }
  };

  const switchYearly = async () => {
    setError(null);
    setSubmitting('switch');
    try {
      const result = await switchToYearly();
      if (result.status === 'redirect') {
        window.location.assign(result.url);
        return;
      }
      navigate('/checkout/success?tier=subscriber');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <header className="text-center">
        <h1 className="text-3xl font-semibold">Membership</h1>
        <p className="mx-auto mt-3 max-w-xl text-textSecondary">
          Badges and themes for your Twitch account, paid monthly, yearly or once.
        </p>
        {me && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--color-borderLight)] bg-white/[0.03] px-3 py-1.5 text-xs text-textSecondary">
            <User className="h-3.5 w-3.5 text-textMuted" />
            <span className="text-textPrimary">@{me.login}</span>
            {current ? (
              <>
                <span className="text-textMuted">·</span>
                <span>
                  {current.interval === 'year' ? 'Yearly' : 'Monthly'} member
                  {shortDate(sub?.current_period_end) &&
                    `, ${sub?.cancel_at_period_end ? 'ends' : 'renews'} ${shortDate(sub?.current_period_end)}`}
                </span>
              </>
            ) : gifted ? (
              <>
                <span className="text-textMuted">·</span>
                <span>Gifted membership{shortDate(sub?.current_period_end) && !sub?.gift_permanent ? ` until ${shortDate(sub?.current_period_end)}` : ''}</span>
              </>
            ) : null}
          </div>
        )}
      </header>

      <div className="flex justify-center">
        <div className="inline-flex rounded-xl border border-[var(--color-borderLight)] bg-white/[0.03] p-1" role="tablist">
          {([['self', 'For you'], ['gift', 'As a gift']] as const).map(([m, label]) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                mode === m ? 'bg-white/[0.09] text-textPrimary' : 'text-textMuted hover:text-textSecondary'
              }`}
            >
              {m === 'gift' && <Gift className="h-3.5 w-3.5" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'gift' ? (
        <GiftSection perMonth={monthlyPrice} perYear={yearlyPrice} paypalEnabled={paypalEnabled} />
      ) : (
        <>
          <div className={`grid grid-cols-1 gap-3 ${yearlyPrice !== null ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`} role="radiogroup" aria-label="How to pay">
            <PlanCard
              selected={plan === 'month'}
              onSelect={() => pickPlan('month')}
              name="Monthly"
              price={`$${monthlyPrice}`}
              unit="/ month"
              detail="Cancel any time"
              tag={current?.interval === 'month' ? 'Your plan' : undefined}
            />
            {yearlyPrice !== null && (
              <PlanCard
                selected={plan === 'year'}
                onSelect={() => pickPlan('year')}
                name="Yearly"
                price={`$${yearlyPrice}`}
                unit="/ year"
                detail={`$${(yearlyPrice / 12).toFixed(2)} a month, save $${yearlySavings}`}
                tag={current?.interval === 'year' ? 'Your plan' : freeMonths > 0 ? `${freeMonths} months free` : undefined}
                accentTag={current?.interval !== 'year'}
              />
            )}
            <PlanCard
              selected={plan === 'once'}
              onSelect={() => pickPlan('once')}
              name="One-time"
              price="Any amount"
              detail={`From $${supporterMin}, paid once`}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1.1fr]">
            <section className="settings-card p-6">
              <div className="flex items-center gap-3">
                <img
                  src={cosmeticUrl(plan === 'once' ? 'streamnook-badge-gold.png' : 'streamnook-badge-gold-animated.webp')}
                  alt=""
                  className="h-10 w-10 object-contain"
                  onError={e => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                />
                <div>
                  <h2 className="text-base font-semibold">{plan === 'once' ? 'Supporter' : 'Subscriber'}</h2>
                  <p className="text-xs text-textMuted">What you get</p>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5">
                {PERKS.map(perk => {
                  const included = plan !== 'once' || perk.once;
                  return (
                    <li key={perk.label} className={`flex items-start gap-2.5 text-sm ${included ? 'text-textPrimary' : 'text-textMuted'}`}>
                      {included
                        ? <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                        : <Minus className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-50" />}
                      <span className={included ? '' : 'line-through decoration-white/20'}>{perk.label}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-5 border-t border-[var(--color-borderLight)] pt-4 text-xs leading-relaxed text-textMuted">
                Badges are yours to keep, even if you cancel.
              </p>
            </section>

            <section className="settings-card flex flex-col p-6">
              <CheckoutPanel
                plan={plan}
                signedIn={Boolean(me)}
                loading={loading}
                current={current}
                periodEnd={shortDate(sub?.current_period_end)}
                monthlyPrice={monthlyPrice}
                yearlyPrice={yearlyPrice}
                supporterAmount={supporterAmount}
                supporterMin={supporterMin}
                setSupporterAmount={setSupporterAmount}
                paypalEnabled={paypalEnabled}
                submitting={submitting}
                onLogin={login}
                onCheckout={checkout}
                onSwitch={switchYearly}
              />
              {error && (
                <p className="mt-3 flex items-start gap-2 text-sm text-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>Couldn't start checkout: {error}</span>
                </p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function PlanCard({
  selected,
  onSelect,
  name,
  price,
  unit,
  detail,
  tag,
  accentTag,
}: {
  selected: boolean;
  onSelect: () => void;
  name: string;
  price: string;
  unit?: string;
  detail: string;
  tag?: string;
  accentTag?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      // Inline because .settings-card is unlayered CSS and outranks Tailwind's
      // border and ring utilities.
      style={selected ? {
        borderColor: 'var(--color-accent-muted)',
        // The accent tint follows the theme token (a season can recolor it).
        background: 'linear-gradient(137deg, color-mix(in srgb, var(--color-accent) 9%, transparent) 0%, color-mix(in srgb, var(--color-accent) 2%, transparent) 70%), linear-gradient(137deg, #111214 4.87%, #0c0d0f 75.88%)',
        boxShadow: '0 0 0 1px var(--color-accent-muted), inset 0 1px 0 0 rgba(255, 255, 255, 0.06)',
      } : undefined}
      className="settings-card relative p-5 text-left transition-[border-color,background] hover:brightness-110"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-textMuted">{name}</span>
        {tag && (
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              accentTag ? 'bg-[var(--color-accent-dim)] text-accent' : 'bg-white/[0.07] text-textSecondary'
            }`}
          >
            {tag}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums">{price}</span>
        {unit && <span className="text-sm text-textMuted">{unit}</span>}
      </div>
      <p className="mt-1 text-xs text-textMuted">{detail}</p>
      <span
        aria-hidden="true"
        className={`absolute bottom-5 right-5 flex h-4 w-4 items-center justify-center rounded-full border ${
          selected ? 'border-accent bg-accent' : 'border-white/20'
        }`}
      >
        {selected && <Check className="h-3 w-3 text-[#08090b]" strokeWidth={3} />}
      </span>
    </button>
  );
}

function CheckoutPanel(props: {
  plan: Plan;
  signedIn: boolean;
  loading: boolean;
  current: ReturnType<typeof currentPlan>;
  periodEnd: string | null;
  monthlyPrice: number;
  yearlyPrice: number | null;
  supporterAmount: number;
  supporterMin: number;
  setSupporterAmount: (n: number) => void;
  paypalEnabled: boolean;
  submitting: Processor | 'switch' | null;
  onLogin: () => void;
  onCheckout: (p: Processor) => void;
  onSwitch: () => void;
}) {
  const { plan, current, periodEnd, monthlyPrice, yearlyPrice, submitting } = props;
  const busy = submitting !== null;

  if (!props.signedIn) {
    return (
      <PanelBody title="Sign in first" body="Your badges are attached to the Twitch account you sign in with.">
        <button onClick={props.onLogin} disabled={props.loading} className="btn-primary w-full px-4 py-2.5 text-sm font-medium">
          <TwitchSignInLabel />
        </button>
      </PanelBody>
    );
  }

  // Already a paying member: the subscription cards describe their plan or a
  // change to it, never a second subscription.
  if (current && plan !== 'once') {
    if (current.interval === plan) {
      return (
        <PanelBody
          title="This is your plan"
          body={periodEnd ? `Next billing date ${periodEnd}. Change your card or cancel from your profile.` : 'Change your card or cancel from your profile.'}
        >
          <Link to="/profile" className="glass-button inline-flex w-full items-center justify-center px-4 py-2.5 text-sm font-medium">
            Manage membership
          </Link>
        </PanelBody>
      );
    }
    if (plan === 'year' && yearlyPrice !== null) {
      const viaPayPal = current.processor === 'paypal';
      return (
        <PanelBody
          title="Switch to yearly"
          body={viaPayPal
            ? `PayPal will ask you to approve the change. $${yearlyPrice} a year starts at your next renewal${periodEnd ? ` on ${periodEnd}` : ''}, and nothing is charged today.`
            : `Your card is charged $${yearlyPrice} today, minus what's left of this month, and renews yearly from here.`}
        >
          <button onClick={props.onSwitch} disabled={busy} className="btn-primary w-full px-4 py-2.5 text-sm font-medium">
            {submitting === 'switch' ? 'Switching…' : viaPayPal ? 'Continue to PayPal' : 'Switch to yearly'}
          </button>
        </PanelBody>
      );
    }
    return (
      <PanelBody
        title="You're on yearly"
        body={`To go monthly, cancel from your profile. Your year runs${periodEnd ? ` until ${periodEnd}` : ' to its end'}, and you can subscribe monthly after that.`}
      >
        <Link to="/profile" className="glass-button inline-flex w-full items-center justify-center px-4 py-2.5 text-sm font-medium">
          Manage membership
        </Link>
      </PanelBody>
    );
  }

  const isOnce = plan === 'once';
  const price = isOnce ? props.supporterAmount : plan === 'year' ? yearlyPrice ?? monthlyPrice : monthlyPrice;
  const summary = isOnce
    ? `$${price} today, nothing after.`
    : plan === 'year'
      ? `$${price} today, then every year until you cancel.`
      : `$${price} today, then every month until you cancel.`;

  return (
    <div className="flex flex-1 flex-col">
      {isOnce && (
        <div className="mb-5">
          <span className="text-xs text-textMuted">Amount (USD)</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {SUPPORTER_PRESETS.map(preset => {
              const active = props.supporterAmount === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => props.setSupporterAmount(preset)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    active
                      ? 'border border-[var(--color-accent-muted)] bg-[var(--color-accent-dim)] text-accent'
                      : 'glass-button'
                  }`}
                >
                  ${preset}
                </button>
              );
            })}
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <span className="text-lg text-textSecondary">$</span>
            <input
              type="number"
              min={props.supporterMin}
              step="1"
              value={props.supporterAmount}
              onChange={e => props.setSupporterAmount(Math.max(props.supporterMin, Number(e.target.value) || props.supporterMin))}
              className="glass-input flex-1 px-3 py-2 text-base"
              aria-label="Amount in US dollars"
            />
          </div>
        </div>
      )}

      <div className="flex items-baseline justify-between border-b border-[var(--color-borderLight)] pb-4">
        <span className="text-sm text-textSecondary">{isOnce ? 'One-time' : plan === 'year' ? 'Yearly membership' : 'Monthly membership'}</span>
        <span className="text-xl font-semibold tabular-nums">${price}</span>
      </div>
      <p className="mt-3 text-xs text-textMuted">{summary}</p>

      <div className="mt-auto grid gap-2 pt-5">
        <button onClick={() => props.onCheckout('card')} disabled={busy} className="pay-button pay-button-card">
          {submitting === 'card' ? 'Redirecting…' : (
            <>
              <span>Pay with card</span>
              <span className="pay-wallets" aria-hidden="true">
                <VisaMark /><MastercardMark /><ApplePayMark /><GooglePayMark />
              </span>
            </>
          )}
        </button>
        {props.paypalEnabled && (
          <button onClick={() => props.onCheckout('paypal')} disabled={busy} className="pay-button pay-button-paypal">
            {submitting === 'paypal' ? 'Redirecting…' : (
              <>
                <PayPalMark className="pay-mark" />
                <span>Pay with PayPal</span>
              </>
            )}
          </button>
        )}
      </div>
      <p className="pay-footnote">
        <StripeMark className="pay-footnote-mark" /> Card payments by Stripe
      </p>
    </div>
  );
}

function PanelBody({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-textSecondary">{body}</p>
      <div className="mt-auto pt-6">{children}</div>
    </div>
  );
}

// Pay once to grant another Twitch user a gifted Subscriber membership. The
// recipient is resolved live by handle; the webhook grants the comp + cosmetics.
function GiftSection({ perMonth, perYear, paypalEnabled }: { perMonth: number; perYear: number | null; paypalEnabled: boolean }) {
  const { me, login } = useAuth();
  const [handle, setHandle] = useState('');
  const [recipient, setRecipient] = useState<GiftRecipient | null>(null);
  const [suggestions, setSuggestions] = useState<GiftRecipient[]>([]);
  const [open, setOpen] = useState(false);
  const [lookup, setLookup] = useState<'idle' | 'resolving' | 'notfound' | 'error'>('idle');
  const [term, setTerm] = useState(1);
  const [submitting, setSubmitting] = useState<Processor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const selectedRef = useRef('');

  const cleaned = handle.trim().toLowerCase().replace(/^@/, '');
  const validFormat = /^[a-z0-9_]{3,25}$/.test(cleaned);

  useEffect(() => {
    // Skip re-searching when the box already holds a recipient we locked onto.
    if (cleaned && cleaned === selectedRef.current) return;
    selectedRef.current = '';
    setRecipient(null);
    setSuggestions([]);
    setOpen(false);
    setLookup('idle');
    setError(null);
    if (cleaned.length < 2) return;

    const id = ++seq.current;
    const t = setTimeout(() => {
      // Registered StreamNook members first (with avatars), then fall back to a
      // Twitch lookup so a not-yet-registered handle can still be gifted.
      searchGiftRecipients(cleaned)
        .then(matches => {
          if (id !== seq.current) return;
          if (matches.length > 0) {
            setSuggestions(matches);
            setOpen(true);
            return;
          }
          if (!validFormat) return;
          setLookup('resolving');
          return resolveGiftRecipient(cleaned).then(r => {
            if (id !== seq.current) return;
            if (!r) {
              setLookup('notfound');
              return;
            }
            selectedRef.current = r.login;
            setHandle(r.login);
            setRecipient(r);
            setLookup('idle');
          });
        })
        .catch(() => {
          if (id === seq.current) setLookup('error');
        });
    }, 250);
    return () => clearTimeout(t);
  }, [cleaned, validFormat]);

  const pick = (u: GiftRecipient) => {
    selectedRef.current = u.login;
    setHandle(u.login);
    setRecipient(u);
    setSuggestions([]);
    setOpen(false);
    setLookup('idle');
  };

  // A 12-month gift costs the same as a yearly plan, matching the server.
  const termPrice = (m: number) => (m === 12 && perYear !== null ? perYear : m * perMonth);
  const total = termPrice(term);
  const canGift = Boolean(recipient && !recipient.is_self);

  const handleGift = async (processor: Processor) => {
    if (!me) {
      login();
      return;
    }
    if (!recipient) return;
    setError(null);
    setSubmitting(processor);
    try {
      const { url } = processor === 'paypal'
        ? await createPayPalGiftCheckout(recipient.login, term)
        : await createGiftCheckout(recipient.login, term);
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(null);
    }
  };

  return (
    <section className="settings-card mx-auto max-w-xl p-6">
      <h2 className="text-base font-semibold">Gift a membership</h2>
      <p className="mt-1 text-sm leading-relaxed text-textSecondary">
        Pay once and another viewer gets the Subscriber membership for the length you pick. It
        does not renew.
      </p>

      <div className="mt-5">
        <span className="text-xs text-textMuted">Recipient's Twitch username</span>
        <div className="relative mt-1.5">
          <div className="flex items-center gap-2">
            <span className="text-lg text-textMuted">@</span>
            <input
              value={handle}
              onChange={e => setHandle(e.target.value)}
              onFocus={() => suggestions.length > 0 && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="glass-input flex-1 px-3 py-2 text-base"
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border border-[var(--color-border)] bg-[rgba(12,13,15,0.97)] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] backdrop-blur-xl">
              {suggestions.map(u => (
                <li key={u.twitch_user_id}>
                  <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => pick(u)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-white/[0.05]"
                  >
                    <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-textPrimary">{u.display_name}</span>
                      <span className="block truncate text-xs text-textMuted">@{u.login}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-2 min-h-[44px]">
        {lookup === 'resolving' && (
          <p className="flex items-center gap-2 text-sm text-textMuted">
            <Loader2 className="h-4 w-4 animate-spin" /> Looking up @{cleaned}…
          </p>
        )}
        {recipient && !recipient.is_self && (
          <div className="flex items-center gap-2.5 rounded-lg border border-[var(--color-borderLight)] bg-white/[0.03] px-3 py-2">
            <img src={recipient.avatar_url} alt="" className="h-8 w-8 rounded-full" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-textPrimary">{recipient.display_name}</p>
              <p className="truncate text-xs text-textMuted">@{recipient.login}</p>
            </div>
            <Check className="ml-auto h-4 w-4 flex-shrink-0 text-accent" />
          </div>
        )}
        {recipient?.is_self && (
          <p className="text-sm text-warning">That's your account. Use For you to subscribe yourself.</p>
        )}
        {lookup === 'notfound' && <p className="text-sm text-textMuted">No Twitch user @{cleaned}.</p>}
        {lookup === 'error' && <p className="text-sm text-error">Couldn't reach Twitch. Try again.</p>}
      </div>

      <div className="mt-4">
        <span className="text-xs text-textMuted">Length</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {[1, 3, 6, 12].map(m => {
            const active = term === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setTerm(m)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  active
                    ? 'border border-[var(--color-accent-muted)] bg-[var(--color-accent-dim)] text-accent'
                    : 'glass-button'
                }`}
              >
                {m} mo · ${termPrice(m)}
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-error">Couldn't start checkout: {error}</p>}

      {!me || !canGift ? (
        <button
          onClick={() => handleGift('card')}
          disabled={submitting !== null || (Boolean(me) && !canGift)}
          className="glass-button mt-5 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium disabled:opacity-55"
        >
          {!me ? 'Sign in with Twitch to gift' : 'Enter a Twitch username'}
        </button>
      ) : (
        <div className="mt-5 grid gap-2">
          <button
            onClick={() => handleGift('card')}
            disabled={submitting !== null}
            className="pay-button pay-button-card"
          >
            {submitting === 'card' ? (
              'Redirecting…'
            ) : (
              <>
                <span>Gift ${total} with card</span>
                <span className="pay-wallets" aria-hidden="true">
                  <VisaMark /><MastercardMark /><ApplePayMark /><GooglePayMark />
                </span>
              </>
            )}
          </button>
          {paypalEnabled && (
            <button
              onClick={() => handleGift('paypal')}
              disabled={submitting !== null}
              className="pay-button pay-button-paypal"
            >
              {submitting === 'paypal' ? (
                'Redirecting…'
              ) : (
                <>
                  <PayPalMark className="pay-mark" />
                  <span>Gift ${total} with PayPal</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
      {me && canGift && (
        <p className="pay-footnote">
          <StripeMark className="pay-footnote-mark" /> Card payments by Stripe. The gift goes to {recipient!.display_name}.
        </p>
      )}
      {me && canGift && (
        <p className="pay-footnote">
          Gifted months add to any they already have. If they pay for a membership by card, their billing pauses for these months and resumes on its own.
        </p>
      )}
    </section>
  );
}
